import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LANGUAGES, CURRENCIES, getTranslations } from '../config/i18n';
import { API_BASE, API } from '../../lib/api';


const GlobalizationContext = createContext();

export function GlobalizationProvider({ children }) {
  // Persist language preference
  const [language, setLanguage] = useState(() => localStorage.getItem('rcq_lang') || 'en');
  const [currency, setCurrency] = useState(() => localStorage.getItem('rcq_currency') || 'INR');
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, INR: 83.25 });
  const [baseConfig, setBaseConfig] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);

  const langConfig = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  const currencyConfig = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
  const translations = getTranslations(language);
  const isRTL = langConfig.dir === 'rtl';

  // Apply direction to document
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = langConfig.locale;
  }, [isRTL, langConfig.locale]);

  // Persist language
  useEffect(() => {
    localStorage.setItem('rcq_lang', language);
  }, [language]);

  // Persist currency
  useEffect(() => {
    localStorage.setItem('rcq_currency', currency);
  }, [currency]);

  // Fetch exchange rates
  useEffect(() => {
    setRatesLoading(true);
    axios.get(`${API}/globalization/exchange-rates`)
      .then(r => setExchangeRates(r.data.rates || {}))
      .catch(() => {})
      .finally(() => setRatesLoading(false));
  }, []);

  // Fetch base config
  useEffect(() => {
    axios.get(`${API}/globalization/base-config`)
      .then(r => setBaseConfig(r.data))
      .catch(() => {});
  }, []);

  // Translate helper: returns translated string
  const tFn = useCallback((key) => {
    return translations[key] || key;
  }, [translations]);

  // Format currency: converts from INR base → selected currency
  const formatCurrency = useCallback((amountInr, opts = {}) => {
    if (typeof amountInr !== 'number') return amountInr;
    const inrRate = exchangeRates['INR'] || 83.25;
    const targetRate = exchangeRates[currency] || 1;
    // Convert: INR → USD → target
    const inUsd = amountInr / inrRate;
    const converted = inUsd * targetRate;
    try {
      return new Intl.NumberFormat(currencyConfig.locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: opts.decimals !== undefined ? opts.decimals : 0,
        maximumFractionDigits: opts.decimals !== undefined ? opts.decimals : 0,
      }).format(converted);
    } catch {
      return `${currencyConfig.symbol}${converted.toFixed(0)}`;
    }
  }, [currency, currencyConfig, exchangeRates]);

  // Format date in locale
  const formatDate = useCallback((date, opts = {}) => {
    try {
      return new Intl.DateTimeFormat(langConfig.locale, {
        weekday: opts.weekday,
        day: 'numeric',
        month: opts.month || 'short',
        year: opts.year !== false ? 'numeric' : undefined,
        hour: opts.time ? '2-digit' : undefined,
        minute: opts.time ? '2-digit' : undefined,
        second: opts.seconds ? '2-digit' : undefined,
        hour12: opts.hour12 !== false,
      }).format(date instanceof Date ? date : new Date(date));
    } catch {
      return String(date);
    }
  }, [langConfig.locale]);

  // Format time only
  const formatTime = useCallback((date) => {
    try {
      return new Intl.DateTimeFormat(langConfig.locale, {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      }).format(date instanceof Date ? date : new Date(date));
    } catch {
      return String(date);
    }
  }, [langConfig.locale]);

  // Get rate: how many units of selected currency per 1 USD
  const getRate = useCallback((fromCode = 'USD') => {
    const fromRate = exchangeRates[fromCode] || 1;
    const toRate = exchangeRates[currency] || 1;
    return toRate / fromRate;
  }, [currency, exchangeRates]);

  return (
    <GlobalizationContext.Provider value={{
      language, setLanguage,
      currency, setCurrency,
      exchangeRates, ratesLoading,
      baseConfig,
      langConfig, currencyConfig,
      translations, isRTL,
      t: tFn,
      formatCurrency,
      formatDate,
      formatTime,
      getRate,
      languages: LANGUAGES,
      currencies: CURRENCIES,
    }}>
      {children}
    </GlobalizationContext.Provider>
  );
}

export function useGlobalization() {
  const ctx = useContext(GlobalizationContext);
  if (!ctx) throw new Error('useGlobalization must be used within GlobalizationProvider');
  return ctx;
}
